/** Stable FNV-1a. Deterministic metadata, never a secret hash. */
export function hashFnv1a(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * Derby Days' only randomness source. Every subsystem gets its own fork so
 * draws in one system can never shift outcomes in another, and fork states
 * serialize into saves so a loaded game continues identically.
 */
export class DerbyRngV1 {
  readonly version = 'derby-rng-v1' as const;
  private state: number;

  constructor(seed: number | string) {
    this.state = typeof seed === 'string' ? hashFnv1a(seed) : seed >>> 0;
    if (this.state === 0) this.state = 0x6d2b79f5;
  }

  next(): number {
    let value = (this.state = (this.state + 0x6d2b79f5) >>> 0);
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  }

  integer(minimum: number, maximumInclusive: number): number {
    if (!Number.isInteger(minimum) || !Number.isInteger(maximumInclusive) || maximumInclusive < minimum) {
      throw new Error('DerbyRngV1.integer requires an ordered integer range.');
    }
    return minimum + Math.floor(this.next() * (maximumInclusive - minimum + 1));
  }

  range(minimum: number, maximum: number): number {
    return minimum + (maximum - minimum) * this.next();
  }

  normal(mean = 0, deviation = 1): number {
    const first = Math.max(Number.EPSILON, this.next());
    const second = this.next();
    return mean + Math.sqrt(-2 * Math.log(first)) * Math.cos(Math.PI * 2 * second) * deviation;
  }

  chance(probability: number): boolean {
    return this.next() < probability;
  }

  pick<T>(values: readonly T[]): T {
    if (values.length === 0) throw new Error('Cannot pick from an empty collection.');
    return values[Math.floor(this.next() * values.length)]!;
  }

  fork(label: string): DerbyRngV1 {
    return new DerbyRngV1((this.state ^ hashFnv1a(label)) >>> 0);
  }

  getState(): number {
    return this.state;
  }

  setState(state: number): void {
    this.state = state >>> 0;
    if (this.state === 0) this.state = 0x6d2b79f5;
  }
}

/** Ranch-sim fork labels (phase 2+). Race sims fork ad hoc per lane. */
export type RngForkLabel = 'schedule' | 'race' | 'training' | 'names';

export const RNG_FORK_LABELS: readonly RngForkLabel[] = ['schedule', 'race', 'training', 'names'];

export type RngForks = Record<RngForkLabel, DerbyRngV1>;

export function createForks(seed: number): RngForks {
  const master = new DerbyRngV1(seed);
  const forks = {} as Record<RngForkLabel, DerbyRngV1>;
  for (const label of RNG_FORK_LABELS) forks[label] = master.fork(label);
  return forks;
}
